import { CssBaseline, ThemeProvider } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/auth';
import { AuthorizationProvider } from '@/authorization';
import { ErrorBoundary } from '@/components/Shared/ErrorBoundary';
import { ConfigurationProvider, useConfiguration } from '@/configuration';
import { FeatureFlagProvider } from '@/feature-flags';
import { IdentityProvider } from '@/iam';
import { NotificationProvider } from '@/notifications';
import { NetworkStatusProvider, NetworkGate } from '@/network';
import { EmbeddedAuthProvider } from '@/platform/authentication';
import { initializePlatformEventPipeline } from '@/platform/events/EventPipeline';
import { embeddedCommunicationService } from '@/services/embeddedCommunicationService';
import { AppQueryProvider } from '@/providers/AppQueryProvider';
import { AppShell, AppShellProvider, useAppShell } from '@/shell';
import { getThemeDefinition } from '@/theme/themeStudioConfig';
import { createAppTheme } from '@/theme/theme';

function AppThemeBridge() {
  const { resolvedTheme, themeStudioPreferences } = useAppShell();
  const { settings } = useConfiguration();

  const selectedTheme = getThemeDefinition(themeStudioPreferences.themeId);

  const effectiveMode =
    themeStudioPreferences.mode === 'system'
      ? resolvedTheme
      : themeStudioPreferences.mode;

  const theme = useMemo(
    () =>
      createAppTheme(effectiveMode, {
        primaryBrandColor:
          themeStudioPreferences.accentColor ||
          settings?.branding.primaryBrandColor ||
          selectedTheme.primary,
        secondaryBrandColor: selectedTheme.secondary,
        backgroundColor: selectedTheme.background,
        surfaceColor: selectedTheme.surface,
        borderRadius: themeStudioPreferences.roundedCorners
          ? selectedTheme.radius
          : 2,
        density: themeStudioPreferences.layoutDensity,
        fontFamily: themeStudioPreferences.fontFamily,
        fontScale: themeStudioPreferences.fontScale,
      }),
    [
      effectiveMode,
      selectedTheme.background,
      selectedTheme.primary,
      selectedTheme.radius,
      selectedTheme.secondary,
      selectedTheme.surface,
      settings?.branding.primaryBrandColor,
      themeStudioPreferences.accentColor,
      themeStudioPreferences.fontFamily,
      themeStudioPreferences.fontScale,
      themeStudioPreferences.layoutDensity,
      themeStudioPreferences.roundedCorners,
    ],
  );

  useEffect(() => {
    initializePlatformEventPipeline();
    const unsubscribeEmbeddedMessages =
      embeddedCommunicationService.initialize();

    return () => {
      unsubscribeEmbeddedMessages();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AppQueryProvider>
          <ErrorBoundary>
            <EmbeddedAuthProvider>
              <FeatureFlagProvider>
                <AuthorizationProvider>
                  <IdentityProvider>
                    <AppShell />
                  </IdentityProvider>
                </AuthorizationProvider>
              </FeatureFlagProvider>
            </EmbeddedAuthProvider>
          </ErrorBoundary>
        </AppQueryProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <AppShellProvider>
      <BrowserRouter>
        <NetworkStatusProvider>
          <NetworkGate>
            <AuthProvider>
              <ConfigurationProvider>
                <AppThemeBridge />
              </ConfigurationProvider>
            </AuthProvider>
          </NetworkGate>
        </NetworkStatusProvider>
      </BrowserRouter>
    </AppShellProvider>
  );
}
